import { useIsMobile } from "@/hooks/use-mobile";
import { formatAmount, getRequiredFields, MONTHS, toApiSort, transactionTypeValues } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetBillingCategoryByCommunity, useGetBillingLogsList } from "@/services";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { formValues, schema } from "./validations";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  SEARCH_CRITERIA,
  SEARCH_TYPE,
} from "../consts";

const useListing = () => {
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState([{ id: "billingLogId", desc: true }]);
  const [month, setMonth] = useState<number | string>("all");
  const [monthKey] = useState<number>(Math.random() * 2);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const form = useForm<formValues>({
    defaultValues: {
      [SEARCH_TYPE]: "",
      [SEARCH_CRITERIA]: "",
    },
    resolver: zodResolver(schema),
  });
  const requiredFields = getRequiredFields(schema);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const currentFinanceYear = useSelector(
    (state: RootState) => state.currentData.currentFinanceYear
  );

  const {
    data: documentList,
    isLoading: isDocLoading,
    refetch: refetchDoc,
    isRefetching: isDocRefetching,
  } = useGetBillingLogsList({
    communityId: communityDetail?.communityId,
    data: {
      filters: [
        {
          field: "billingYear",
          value: currentFinanceYear,
          operator: "EQUAL",
        },
        ...(month != "all"
          ? [
            {
              field: "billingPeriod",
              value: month,
              operator: "EQUAL",
            },
          ]
          : []),
        {
          field: "communityId",
          value: communityDetail?.communityId,
          operator: "EQUAL",
        },
        ...(form.watch()[SEARCH_TYPE]
          ? [
            {
              field: form.watch()[SEARCH_TYPE],
              value: [
                moment(form.watch()[SEARCH_CRITERIA]?.from).format(
                  "YYYY-MM-DDT00:00:00"
                ),
                moment(form.watch()[SEARCH_CRITERIA]?.to).format(
                  "YYYY-MM-DDT23:59:00"
                ),
              ],
              operator: "BETWEEN",
            },
          ]
          : []),
      ],
      pageNumber,
      pageSize,
      sort: toApiSort(sorting, { field: "billingLogId", direction: "DESC" }),
      searchFields: ["billingLogId", "updatedByUserName", "postDocumentHeaderId", "documentNumber",
        "parkedDocumentHeaderId", "billingPeriod", "transactionType",
        "totalOwnerships"],
      search: globalFilter
    },
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  useEffect(() => {
    refetchDoc();
  }, [pageNumber, pageSize, sorting]);

  useEffect(() => {
    if (globalFilter?.length >= 2 || globalFilter?.length == 0) {
      refetchDoc();
    }
  }, [globalFilter]);

  const onSubmit = () => {
    form.setValue(SEARCH_TYPE, "createdAt");
    setTimeout(() => refetchDoc(), 100);
  };

  const onClear = () => {
    form.reset();
    setTimeout(() => refetchDoc());
  };

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.admin}/${routes.activityDashboard}`,
        name: `Activity Dashboard - ${communityDetail?.name || ""}`,
      },
      {
        link: "",
        name: <span className="text-primary">Billing Log</span>,
      },
    ],
    []
  );

  const { data: billingCategory, isLoading: isLoadingBillingCategory } =
    useGetBillingCategoryByCommunity({ communityId: communityDetail?.communityId });

  // Define Table Columns
  const columns = [
    {
      accessorKey: "billingLogId",
      header: <div>Job ID</div>,
      accessorFn: (row: any) => String(row?.billingLogId || ""),
    },
    {
      accessorKey: "parkedDocumentHeaderId",
      header: <div>Park Doc.ID</div>,
      accessorFn: (row: any) => String(row?.parkedDocumentHeaderId || ""),
    },
    {
      accessorKey: "documentNumber",
      header: <div>Post Doc.ID</div>,
      accessorFn: (row: any) => String(row?.documentNumber || ""),
    },
    {
      accessorKey: "updatedByUserName",
      header: <div>Username</div>,
      accessorFn: (row: any) => String(row?.updatedByUserName || ""),
    },
    {
      accessorKey: "billingCategoryIds",
      header: <div>Billing Category</div>,
      accessorFn: (row: any) =>
        row.billingCategoryIds?.split(",")?.map((data: any) => {
          return (
            billingCategory?.data?.find(
              (val: any) =>
                data?.toString() == val.billingCategoryId?.toString()
            )?.categoryName || ""
          );
        }),
    },
    {
      accessorKey: "billingPeriod",
      header: <div>Billing Period</div>,
      accessorFn: (row: any) =>
        row?.billingPeriod ? MONTHS[row?.billingPeriod - 1] : "",
    },
    {
      accessorKey: "updatedAt",
      header: <div>Run Date</div>,
      accessorFn: (row: any) =>
        row?.updatedAt
          ? moment(row?.updatedAt || "").format("MM/DD/YYYY HH:mm")
          : "",
    },
    {
      accessorKey: "transactionType",
      header: <div>Transaction Type</div>,
      accessorFn: (row: any) =>
        (transactionTypeValues as any)[String(row?.transactionType || "")],
    },
    {
      accessorKey: "totalOwnerships",
      header: <div># Processed Properties</div>,
      accessorFn: (row: any) => String(row?.totalOwnerships || "0"),
    },
    {
      accessorKey: "successfullyBilled",
      header: <div>Total Revenue Generated</div>,
      accessorFn: (row: any) => formatAmount(row?.successfullyBilled || "0"),
    },
  ];

  const onRowClick = (row: any) => {
    navigate(`${row.original.parkedDocumentHeaderId}/view`);
  };

  const onMonthChange = (val: any) => {
    setMonth(val);
    setTimeout(() => refetchDoc(), 100);
  };

  return {
    breadcrumbData,
    columns,
    globalFilter,
    setGlobalFilter,
    isMobile,
    onRowClick,
    isDocLoading,
    documentList,
    isDocRefetching,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    form,
    requiredFields,
    onSubmit,
    onClear,
    communityDetail,
    onMonthChange,
    monthKey,
    month,
    isLoadingBillingCategory,
    currentFinanceYear,
    sorting,
    setSorting,
  };
};

export default useListing;
